import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import CustomButton from "../../components/CustomButton";
import Link from "@mui/material/Link";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import paths from "../../constants/paths";
import { useAuth } from "../../contexts/Auth";
import isEmail from "validator/lib/isEmail";
import { SUCCESS, DEACTIVATED } from "../../constants/common";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ShieldExclamation } from "../../assets/Icons/shield-exclamation.svg";
import { trackClickEvent } from "../../mixpanel";
import "./style.css";
import Modal from "@mui/material/Modal";
import { LoadingButton } from "@mui/lab";

const Login = () => {
  const navigate = useNavigate();

  const {
    signIn,
    loginStatus,
    updateAuthState,
    isLogginBlocked,
    countDown,
    signInSSO,
  } = useAuth();

  const initialState = {
    email: "",
    password: "",
  };

  const [state, setState] = useState(initialState);
  const [isInValid, setIsInValid] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [showDeactivatedModal, setShowDeactivatedModal] = useState(false);

  const handleInput = (ev) => {
    setState((prevState) => ({
      ...prevState,
      [ev.target.name]: ev.target.value,
    }));
  };

  const isValid = () => {
    return state.email && isEmail(state.email) && state.password;
  };
  
  const handleSSOLogin = async () => {
    setIsDisabled(true);
    try {
      const { redirect_url } = await signInSSO(); 
      window.location.href = redirect_url;
  } catch (error) {
    console.error(error);
    setIsDisabled(false);
  }
};

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    try {
      if (isValid()) {
        setIsDisabled(true);
        const response = await signIn(state);

        trackClickEvent("User Login", {
          page: "Login",
          component: "Login Form",
          button: "Login",
        });
        setIsDisabled(false);
      } else {
        setIsInValid(true);
      }
    } catch (err) {
      console.log(err);
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    if (loginStatus === DEACTIVATED) {
      setShowDeactivatedModal(true);
    }
  }, [loginStatus, updateAuthState]);

  const handleCloseModal = () => {
    setShowDeactivatedModal(false);
    updateAuthState("loginStatus", "");
    setState(initialState);
  };

  const handleSendMail = () => {
    const supportEmail = "team@thettn.com";
    const subject = "Account Reactivation Request";
    const body = `Hello TTN Team,\n\nI would like to request reactivation of my account.\n\nEmail: ${state?.email}\n\nThank you.`;
    const mailtoLink = `mailto:${supportEmail}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    setShowDeactivatedModal(false);
    setState(initialState);

    trackClickEvent("Send Reactivation Email", {
      page: "Login",
      component: "Deactivated Account Modal",
      button: "Send Email",
    });
  };

  return (
    <Container className="hideShadow" maxWidth="xs">
      <div className="formStyle">
        <Typography variant="h3" sx={{ marginBottom: "8px" }} mb={2}>
          Sign in
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: "26px" }}>
          Welcome back
        </Typography>
        <Box mb={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label="Email"
                type="email"
                fullWidth
                onChange={handleInput}
                value={state.email}
                name="email"
                error={isInValid && (!state.email || !isEmail(state.email))}
                helperText={
                  isInValid && !isEmail(state.email)
                    ? "Enter a valid email address"
                    : ""
                }
                disabled={isLogginBlocked}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label="Password"
                type="password"
                fullWidth
                onChange={handleInput}
                value={state.password}
                name="password"
                error={isInValid && !state.password}
                helperText={
                  isInValid && !state.password ? "Password is required" : ""
                }
                disabled={isLogginBlocked}
              />
            </Grid>

            {/* Sign In button */}
            <Grid item xs={12}>
              <CustomButton
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={isLogginBlocked || isDisabled}
                onClick={handleSubmit}
                name="Sign in"
                eventName="Sign-in button clicked"
              >
                Sign In
              </CustomButton>
            </Grid>
            <Grid item xs={12}>
              <CustomButton
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={isLogginBlocked || isDisabled}
                onClick={handleSSOLogin}
                name="Login with SSO"
                eventName="Sign-in-sso button clicked"
              >
                Sign In With SSO
              </CustomButton>
            </Grid>
          </Grid>
        </Box>

        {isLogginBlocked && (
          <Alert
            severity="error"
            sx={{
              backgroundColor: "#FAD4D4",
              borderRadius: 1,
              border: "1px solid #EE6363",
              mb: 1.5,
              mt: -1.5,
              py: 0.5,
              px: 1,
              alignItems: "center",
              color: "text.primary",
            }}
            icon={<ShieldExclamation />}
          >
            Security Lockout! Try again in {countDown} minutes.
          </Alert>
        )}

        <Typography textAlign="center" variant="body2" fontWeight={600}>
          <Link
            onClick={() =>
              trackClickEvent("Forgot Password link clicked", {
                button: "Forgot Password",
                page: "Login",
                component: "Sign-in",
              })
            }
            href={paths.forgetPassword}
          >
            Forgot Password{" "}
          </Link>
          or{" "}
          <Link
            onClick={() =>
              trackClickEvent("Create an account link clicked", {
                button: "Create an account",
                page: "Login",
                component: "Sign-in",
              })
            }
            href={paths.onboarding}
          >
            Create an account
          </Link>
        </Typography>
      </div>

      <Modal
        open={showDeactivatedModal}
        onClose={handleCloseModal}
        aria-labelledby="deactivated-account-modal"
        aria-describedby="deactivated-account-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 700,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2.5,
          }}
        >
          <Typography
            id="deactivated-modal-title"
            variant="h4"
            component="h2"
            mb={2}
          >
            Your account is currently deactivated.
          </Typography>
          <Typography
            id="deactivated-modal-description"
            variant="body1"
            mb={6}
            color="#61676B"
          >
            You won’t be able to log in while your account is deactivated. If
            you’d like to rejoin, please email us at{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleSendMail();
              }}
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              team@thettn.com
            </a>
            , and we’re happy to help you reactivate your account.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <LoadingButton
              onClick={handleCloseModal}
              sx={{
                color: "#A3A6A8",
                backgroundColor: "#F1F2F3",
              }}
            >
              Cancel
            </LoadingButton>
            <LoadingButton variant="contained" onClick={handleSendMail}>
              Send Email
            </LoadingButton>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default Login;
